import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "master_admin" | "association_admin" | "admin" | "moderator" | "user";

export interface UserPermissions {
  role_type: string | null;
  workspace_roles: string[];
  permissions: string[];
  is_admin: boolean;
  display_name?: string;
  job_title?: string;
}

/** Shape returned by the get_user_org_permissions RPC */
interface OrgPermissionsResult {
  role_type?: string | null;
  is_admin?: boolean;
  workspace_roles?: string[] | string;
  permissions?: string[];
  display_name?: string;
  job_title?: string;
}

/**
 * Fetches the user's role and permissions for the active org
 * using the unified get_user_org_permissions resolver.
 * Falls back to legacy path if new tables have no data.
 */
export const useUserRole = () => {
  const { user } = useAuth();
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["user_role", user?.id, orgId],
    queryFn: async (): Promise<AppRole | null> => {
      if (!user) return null;

      // 1. Try unified permission resolver (reads both new + legacy tables)
      if (orgId) {
        const { data: perms, error: permsError } = await supabase.rpc("get_user_org_permissions", {
          _user_id: user.id,
          _org_id: orgId,
        });

        if (!permsError && perms) {
          const p = perms as unknown as OrgPermissionsResult;
          if (p.role_type === "master_admin") return "master_admin";
          if (p.role_type === "association_admin") return "association_admin";
          if (p.is_admin) return "admin";
          if (p.role_type === "org_staff") return "moderator";
          if (p.role_type === "member_portal_user") return "user";
          if (p.role_type) return "user";
        }
      }

      // 2. Check user_roles table (master_admin etc.) — prefer highest privilege
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id);

      if (roles && roles.length > 0) {
        const roleOrder: AppRole[] = ["master_admin", "association_admin", "admin", "moderator", "user"];
        for (const r of roleOrder) {
          if (roles.some((row) => row.role === r)) return r;
        }
        return roles[0].role as AppRole;
      }

      // 3. No role found
      return null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetches full permission object for the active org context.
 */
export const useUserPermissions = () => {
  const { user } = useAuth();
  const { orgId } = useOrg();

  return useQuery({
    queryKey: ["user_permissions", user?.id, orgId],
    queryFn: async (): Promise<UserPermissions> => {
      const empty: UserPermissions = {
        role_type: null,
        workspace_roles: [],
        permissions: [],
        is_admin: false,
      };
      if (!user || !orgId) return empty;

      const { data, error } = await supabase.rpc("get_user_org_permissions", { _user_id: user.id, _org_id: orgId });

      if (error || !data) return empty;
      const d = data as unknown as OrgPermissionsResult;
      return {
        role_type: d.role_type ?? null,
        workspace_roles: d.workspace_roles
          ? Array.isArray(d.workspace_roles)
            ? d.workspace_roles
            : JSON.parse(d.workspace_roles as string)
          : [],
        permissions: d.permissions ?? [],
        is_admin: !!d.is_admin,
        display_name: d.display_name,
        job_title: d.job_title,
      };
    },
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useIsAdmin = () => {
  const { data: role, isLoading } = useUserRole();
  return {
    isAdmin: role === "admin" || role === "master_admin" || role === "association_admin",
    isMasterAdmin: role === "master_admin",
    isAssociationAdmin: role === "association_admin",
    role,
    isLoading,
  };
};
